import { useState, useEffect, useCallback, useRef } from 'react';
import { qa, pm } from '../../services/api';
import { theme } from '../../theme';

// ── Meta ──────────────────────────────────────────────────────────────────────
const PRIORITY_META = {
  high:   { label: 'High',   color: '#DC2626', bg: '#FEF2F2' },
  medium: { label: 'Medium', color: '#D97706', bg: '#FFFBEB' },
  low:    { label: 'Low',    color: '#16A34A', bg: '#F0FDF4' },
};
const TYPE_META = {
  functional:  { label: 'Functional',  color: '#2563EB', bg: '#DBEAFE' },
  regression:  { label: 'Regression',  color: '#7C3AED', bg: '#F5F3FF' },
  uat:         { label: 'UAT',         color: '#0D9488', bg: '#F0FDFA' },
  performance: { label: 'Performance', color: '#EA580C', bg: '#FFF7ED' },
  smoke:       { label: 'Smoke',       color: '#9A3412', bg: '#FFF7ED' },
  sanity:      { label: 'Sanity',      color: '#6B7280', bg: '#F3F4F6' },
};
const EXEC_STATUS_META = {
  not_run: { label: 'Not Run', color: '#6B7280', bg: '#F3F4F6', icon: '○' },
  pass:    { label: 'Pass',    color: '#16A34A', bg: '#DCFCE7', icon: '✓' },
  fail:    { label: 'Fail',    color: '#DC2626', bg: '#FEE2E2', icon: '✗' },
  blocked: { label: 'Blocked', color: '#D97706', bg: '#FEF3C7', icon: '⛔' },
  retest:  { label: 'Retest',  color: '#7C3AED', bg: '#F5F3FF', icon: '↺' },
};
const CYCLE_STATUS_META = {
  draft:       { label: 'Draft',       color: '#6B7280', bg: '#F3F4F6' },
  active:      { label: 'Active',      color: '#2563EB', bg: '#DBEAFE' },
  in_progress: { label: 'In Progress', color: '#D97706', bg: '#FEF3C7' },
  completed:   { label: 'Completed',   color: '#16A34A', bg: '#DCFCE7' },
  closed:      { label: 'Closed',      color: '#374151', bg: '#E5E7EB' },
};

function PriBadge({ v }) {
  const m = PRIORITY_META[v] || { label: v, color: '#6B7280', bg: '#F3F4F6' };
  return <span style={{ background: m.bg, color: m.color, padding: '2px 8px', borderRadius: 9999, fontSize: '0.7rem', fontWeight: 700 }}>{m.label}</span>;
}
function TypeBadge({ v }) {
  const m = TYPE_META[v] || { label: v, color: '#6B7280', bg: '#F3F4F6' };
  return <span style={{ background: m.bg, color: m.color, padding: '2px 8px', borderRadius: 9999, fontSize: '0.7rem', fontWeight: 600 }}>{m.label}</span>;
}
function ExecBadge({ v }) {
  const m = EXEC_STATUS_META[v] || EXEC_STATUS_META.not_run;
  return <span style={{ background: m.bg, color: m.color, padding: '2px 8px', borderRadius: 9999, fontSize: '0.7rem', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 4 }}>{m.icon} {m.label}</span>;
}
function CycleBadge({ v }) {
  const m = CYCLE_STATUS_META[v] || CYCLE_STATUS_META.draft;
  return <span style={{ background: m.bg, color: m.color, padding: '2px 8px', borderRadius: 9999, fontSize: '0.7rem', fontWeight: 700 }}>{m.label}</span>;
}

const inp = { width: '100%', padding: '0.55rem 0.75rem', border: `1.5px solid ${theme.border}`, borderRadius: 6, fontSize: '0.875rem', outline: 'none', boxSizing: 'border-box', color: theme.text };
const lbl = { display: 'block', fontSize: '0.78rem', fontWeight: 600, color: theme.text, marginBottom: '0.3rem' };
const card = { background: '#fff', border: `1px solid ${theme.border}`, borderRadius: 10, padding: '1.25rem' };

function ProgressBar({ pass, fail, blocked, retest, total }) {
  if (!total) return <div style={{ height: 6, background: '#F3F4F6', borderRadius: 9999 }} />;
  const not_run = Math.max(total - pass - fail - blocked - retest, 0);
  const segs = [
    { v: pass,    color: '#16A34A' },
    { v: fail,    color: '#DC2626' },
    { v: blocked, color: '#D97706' },
    { v: retest,  color: '#7C3AED' },
    { v: not_run, color: '#E5E7EB' },
  ].filter(s => s.v > 0);
  return (
    <div style={{ height: 6, background: '#E5E7EB', borderRadius: 9999, overflow: 'hidden', display: 'flex' }}>
      {segs.map((s, i) => (
        <div key={i} style={{ width: `${(s.v / total) * 100}%`, background: s.color }} />
      ))}
    </div>
  );
}

// ── Test Case Modal ───────────────────────────────────────────────────────────
function TestCaseModal({ tc, projectId, onClose, onSaved }) {
  const isNew = !tc;
  const [form, setForm] = useState(tc ? {
    title: tc.title || '',
    module_name: tc.module_name || tc.module_feature || '',
    submodule_name: tc.submodule_name || '',
    scenario_description: tc.scenario_description || tc.title || '',
    priority: tc.priority || 'medium',
    test_type: tc.test_type || 'functional',
    steps: tc.steps?.length ? tc.steps : [{ step: '', expected: '' }],
    expected_result: tc.expected_result || '',
    scenario_fk: tc.scenario_fk || '',
  } : {
    title: '', module_name: '', submodule_name: '', scenario_description: '',
    priority: 'medium', test_type: 'functional',
    steps: [{ step: '', expected: '' }],
    expected_result: '', scenario_fk: '',
  });
  const [saving, setSaving] = useState(false);

  const setStep = (i, field, val) => {
    const s = [...form.steps];
    s[i] = { ...s[i], [field]: val };
    setForm({ ...form, steps: s });
  };
  const addStep = () => setForm({ ...form, steps: [...form.steps, { step: '', expected: '' }] });
  const removeStep = (i) => setForm({ ...form, steps: form.steps.filter((_, idx) => idx !== i) });

  const handleSave = async () => {
    if (!form.module_name.trim()) { alert('Module Name is required'); return; }
    setSaving(true);
    try {
      const payload = { ...form, title: form.scenario_description || form.module_name, steps: form.steps.filter(s => s.step.trim()) };
      if (isNew) await qa.createTestCase(projectId, payload);
      else await qa.updateTestCase(tc.id, payload);
      onSaved();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to save test case.');
    } finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 680, maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        <div style={{ padding: '1.25rem 1.5rem', borderBottom: `1px solid ${theme.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 700 }}>{isNew ? 'New Test Case' : `Edit — ${tc.tc_id}`}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem', color: '#9CA3AF' }}>×</button>
        </div>
        <div style={{ padding: '1.25rem 1.5rem', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.875rem' }}>
            <div>
              <label style={lbl}>Module Name *</label>
              <input style={inp} value={form.module_name} onChange={e => setForm({ ...form, module_name: e.target.value })} placeholder="e.g. Authentication" />
            </div>
            <div>
              <label style={lbl}>Submodule Name</label>
              <input style={inp} value={form.submodule_name} onChange={e => setForm({ ...form, submodule_name: e.target.value })} placeholder="e.g. Login" />
            </div>
            <div style={{ gridColumn: '1/-1' }}>
              <label style={lbl}>Test Scenario Description *</label>
              <textarea style={{ ...inp, minHeight: 60, resize: 'vertical' }} value={form.scenario_description} onChange={e => setForm({ ...form, scenario_description: e.target.value })} placeholder="Describe the test scenario" />
            </div>
            <div>
              <label style={lbl}>Priority</label>
              <select style={inp} value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}>
                {Object.entries(PRIORITY_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <div>
              <label style={lbl}>Test Type</label>
              <select style={inp} value={form.test_type} onChange={e => setForm({ ...form, test_type: e.target.value })}>
                {Object.entries(TYPE_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
          </div>

          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
              <label style={{ ...lbl, margin: 0 }}>Test Steps</label>
              <button onClick={addStep} style={{ background: 'none', border: `1px solid ${theme.border}`, borderRadius: 6, padding: '2px 10px', cursor: 'pointer', fontSize: '0.78rem', color: theme.primary, fontWeight: 600 }}>+ Add Step</button>
            </div>
            <div style={{ border: `1px solid ${theme.border}`, borderRadius: 8, overflow: 'hidden' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '32px 1fr 1fr 32px', background: '#F8FAFC', padding: '0.5rem 0.75rem', borderBottom: `1px solid ${theme.border}` }}>
                {['#', 'Step / Action', 'Expected Result', ''].map((h, i) => (
                  <span key={i} style={{ fontSize: '0.72rem', fontWeight: 700, color: theme.textMuted, textTransform: 'uppercase' }}>{h}</span>
                ))}
              </div>
              {form.steps.map((s, i) => (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '32px 1fr 1fr 32px', padding: '0.5rem 0.75rem', borderBottom: i < form.steps.length - 1 ? `1px solid ${theme.border}` : 'none', alignItems: 'start', gap: '0.5rem' }}>
                  <span style={{ fontSize: '0.78rem', color: theme.textMuted, paddingTop: '0.55rem', fontWeight: 600 }}>{i + 1}</span>
                  <textarea style={{ ...inp, minHeight: 50, resize: 'vertical', marginBottom: 0 }} value={s.step} onChange={e => setStep(i, 'step', e.target.value)} placeholder="Action to perform" />
                  <textarea style={{ ...inp, minHeight: 50, resize: 'vertical', marginBottom: 0 }} value={s.expected} onChange={e => setStep(i, 'expected', e.target.value)} placeholder="Expected outcome" />
                  <button onClick={() => removeStep(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#DC2626', fontSize: '1rem', paddingTop: '0.4rem' }} disabled={form.steps.length === 1}>×</button>
                </div>
              ))}
            </div>
          </div>

          <div>
            <label style={lbl}>Expected Result (Overall)</label>
            <textarea style={{ ...inp, minHeight: 60, resize: 'vertical' }} value={form.expected_result} onChange={e => setForm({ ...form, expected_result: e.target.value })} placeholder="Overall expected outcome of this test case" />
          </div>
        </div>
        <div style={{ padding: '1rem 1.5rem', borderTop: `1px solid ${theme.border}`, display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
          <button onClick={onClose} style={{ padding: '0.5rem 1.25rem', border: `1.5px solid ${theme.border}`, borderRadius: 7, background: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{ padding: '0.5rem 1.25rem', border: 'none', borderRadius: 7, background: theme.primary, color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem', opacity: saving ? 0.7 : 1 }}>{saving ? 'Saving…' : isNew ? 'Create' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}

// ── Cycle Modal ───────────────────────────────────────────────────────────────
function CycleModal({ cycle, projectId, onClose, onSaved }) {
  const isNew = !cycle;
  const [form, setForm] = useState(cycle ? {
    name: cycle.name, release_version: cycle.release_version || '',
    start_date: cycle.start_date || '', end_date: cycle.end_date || '',
    description: cycle.description || '', status: cycle.status || 'draft',
  } : { name: '', release_version: '', start_date: '', end_date: '', description: '', status: 'draft' });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.name.trim()) { alert('Cycle name is required'); return; }
    setSaving(true);
    try {
      if (isNew) await qa.createCycle(projectId, form);
      else await qa.updateCycle(cycle.id, form);
      onSaved();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to save cycle.');
    } finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 500, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        <div style={{ padding: '1.25rem 1.5rem', borderBottom: `1px solid ${theme.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 700 }}>{isNew ? 'New Test Cycle' : 'Edit Cycle'}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem', color: '#9CA3AF' }}>×</button>
        </div>
        <div style={{ padding: '1.25rem 1.5rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <div>
            <label style={lbl}>Cycle Name *</label>
            <input style={inp} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Sprint 5 Regression" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <div>
              <label style={lbl}>Release Version</label>
              <input style={inp} value={form.release_version} onChange={e => setForm({ ...form, release_version: e.target.value })} placeholder="e.g. v2.4.0" />
            </div>
            <div>
              <label style={lbl}>Status</label>
              <select style={inp} value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                {Object.entries(CYCLE_STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <div>
              <label style={lbl}>Start Date</label>
              <input style={inp} type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} />
            </div>
            <div>
              <label style={lbl}>End Date</label>
              <input style={inp} type="date" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} />
            </div>
          </div>
          <div>
            <label style={lbl}>Description</label>
            <textarea style={{ ...inp, minHeight: 70, resize: 'vertical' }} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="What is this cycle testing?" />
          </div>
        </div>
        <div style={{ padding: '1rem 1.5rem', borderTop: `1px solid ${theme.border}`, display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
          <button onClick={onClose} style={{ padding: '0.5rem 1.25rem', border: `1.5px solid ${theme.border}`, borderRadius: 7, background: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{ padding: '0.5rem 1.25rem', border: 'none', borderRadius: 7, background: theme.primary, color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem', opacity: saving ? 0.7 : 1 }}>{saving ? 'Saving…' : isNew ? 'Create' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}

// ── Add Cases to Cycle Modal ──────────────────────────────────────────────────
function AddCasesModal({ cycle, projectId, existingIds, modules, members, onClose, onSaved }) {
  const [tab, setTab] = useState('cases');         // 'cases' | 'module' | 'story'
  const [allCases, setAllCases] = useState([]);
  const [stories, setStories] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [selectedModuleId, setSelectedModuleId] = useState('');
  const [selectedStoryId, setSelectedStoryId] = useState('');
  const [assigneeId, setAssigneeId] = useState('');
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    qa.getTestCases(projectId, { is_active: true }).then(r => {
      setAllCases((r.data || []).filter(tc => !existingIds.has(tc.id)));
    }).catch(() => {});
    pm.getIssues(projectId, { type: 'story' }).then(r => setStories(r.data || [])).catch(() => {});
  }, [projectId, existingIds]);

  const filteredCases = allCases.filter(tc =>
    tc.title.toLowerCase().includes(search.toLowerCase()) ||
    (tc.module_name || tc.module_feature || '').toLowerCase().includes(search.toLowerCase()) ||
    (tc.tc_id || '').toLowerCase().includes(search.toLowerCase())
  );

  const toggle = id => {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  };

  const handleAdd = async () => {
    setSaving(true);
    try {
      if (tab === 'cases') {
        if (!selected.size) { alert('Select at least one test case'); setSaving(false); return; }
        await qa.addCasesToCycle(cycle.id, { test_case_ids: [...selected], assignee_id: assigneeId ? Number(assigneeId) : null });
      } else if (tab === 'module') {
        if (!selectedModuleId) { alert('Select a module'); setSaving(false); return; }
        await qa.addCasesToCycle(cycle.id, { by_module_id: Number(selectedModuleId), assignee_id: assigneeId ? Number(assigneeId) : null });
      } else {
        if (!selectedStoryId) { alert('Select a story'); setSaving(false); return; }
        await qa.addCasesToCycle(cycle.id, { by_story_id: Number(selectedStoryId), assignee_id: assigneeId ? Number(assigneeId) : null });
      }
      onSaved();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to add cases.');
    } finally { setSaving(false); }
  };

  const tabBtn = (k, label) => (
    <button key={k} onClick={() => setTab(k)} style={{ padding: '0.4rem 0.875rem', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: '0.8rem', fontWeight: tab === k ? 700 : 500, background: tab === k ? '#fff' : 'transparent', color: tab === k ? theme.text : theme.textMuted, boxShadow: tab === k ? '0 1px 3px rgba(0,0,0,0.08)' : 'none' }}>{label}</button>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 640, maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        <div style={{ padding: '1.25rem 1.5rem', borderBottom: `1px solid ${theme.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 700 }}>Add Test Cases to Cycle</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem', color: '#9CA3AF' }}>×</button>
        </div>

        {/* Tab bar + assignee */}
        <div style={{ padding: '0.75rem 1.5rem', borderBottom: `1px solid ${theme.border}`, display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: '0.25rem', background: '#F3F4F6', borderRadius: 8, padding: '3px', flex: 1 }}>
            {tabBtn('cases', '☑ By Test Case')}
            {tabBtn('module', '▦ By Module')}
            {tabBtn('story', '◉ By Story')}
          </div>
          <div style={{ minWidth: 160 }}>
            <select style={{ ...inp, margin: 0 }} value={assigneeId} onChange={e => setAssigneeId(e.target.value)}>
              <option value="">Assign Tester (optional)</option>
              {members.map(m => <option key={m.employee_id} value={m.employee_id}>{m.name || m.employee_name}</option>)}
            </select>
          </div>
        </div>

        {/* Tab content */}
        {tab === 'cases' && (
          <>
            <div style={{ padding: '0.75rem 1.5rem', borderBottom: `1px solid ${theme.border}` }}>
              <input style={inp} placeholder="Search by title, module or TC-ID…" value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <div style={{ flex: 1, overflowY: 'auto' }}>
              {filteredCases.length === 0 ? (
                <p style={{ textAlign: 'center', color: theme.textMuted, padding: '2rem', fontSize: '0.875rem' }}>No test cases available</p>
              ) : filteredCases.map(tc => (
                <label key={tc.id} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', padding: '0.75rem 1.5rem', cursor: 'pointer', borderBottom: `1px solid #F3F4F6` }}
                  onMouseEnter={e => e.currentTarget.style.background = '#F8FAFC'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <input type="checkbox" checked={selected.has(tc.id)} onChange={() => toggle(tc.id)} style={{ marginTop: 2, accentColor: theme.primary }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '0.72rem', fontFamily: 'monospace', color: theme.textMuted, fontWeight: 600 }}>{tc.tc_id}</span>
                      <PriBadge v={tc.priority} /><TypeBadge v={tc.test_type} />
                    </div>
                    <div style={{ fontWeight: 600, fontSize: '0.875rem', marginTop: 2 }}>{tc.title}</div>
                    {(tc.module_name || tc.module_feature) && <div style={{ fontSize: '0.75rem', color: theme.textMuted, marginTop: 1 }}>{tc.module_name || tc.module_feature}</div>}
                  </div>
                </label>
              ))}
            </div>
          </>
        )}

        {tab === 'module' && (
          <div style={{ flex: 1, padding: '1.25rem 1.5rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <p style={{ margin: 0, fontSize: '0.875rem', color: theme.textMuted }}>All active test cases in the selected module will be added.</p>
            <select style={inp} value={selectedModuleId} onChange={e => setSelectedModuleId(e.target.value)}>
              <option value="">— Select Module —</option>
              {modules.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </div>
        )}

        {tab === 'story' && (
          <div style={{ flex: 1, padding: '1.25rem 1.5rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <p style={{ margin: 0, fontSize: '0.875rem', color: theme.textMuted }}>All active test cases linked to the selected story will be added.</p>
            <select style={inp} value={selectedStoryId} onChange={e => setSelectedStoryId(e.target.value)}>
              <option value="">— Select Story —</option>
              {stories.map(s => <option key={s.id} value={s.id}>{s.title}</option>)}
            </select>
          </div>
        )}

        <div style={{ padding: '1rem 1.5rem', borderTop: `1px solid ${theme.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: '0.8rem', color: theme.textMuted }}>
            {tab === 'cases' ? `${selected.size} selected` : tab === 'module' ? (selectedModuleId ? `Module: ${modules.find(m => m.id === Number(selectedModuleId))?.name}` : 'No module selected') : (selectedStoryId ? `Story selected` : 'No story selected')}
          </span>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button onClick={onClose} style={{ padding: '0.5rem 1.25rem', border: `1.5px solid ${theme.border}`, borderRadius: 7, background: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem' }}>Cancel</button>
            <button onClick={handleAdd} disabled={saving} style={{ padding: '0.5rem 1.25rem', border: 'none', borderRadius: 7, background: theme.primary, color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem', opacity: saving ? 0.6 : 1 }}>{saving ? 'Adding…' : 'Add to Cycle'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Execution Run Modal ────────────────────────────────────────────────────────
function RunModal({ exec, members, onClose, onSaved }) {
  const [form, setForm] = useState({
    status: exec.status || 'not_run',
    actual_result: exec.actual_result || '',
    comments: exec.comments || '',
    time_spent_mins: exec.time_spent_mins || '',
    bug_id: exec.bug_id || '',
    assigned_to: exec.assigned_to || '',
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form, time_spent_mins: form.time_spent_mins ? Number(form.time_spent_mins) : null, bug_id: form.bug_id ? Number(form.bug_id) : null, assigned_to: form.assigned_to ? Number(form.assigned_to) : null };
      await qa.updateExecution(exec.id, payload);
      onSaved();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to update execution.');
    } finally { setSaving(false); }
  };

  const tc = { title: exec.title, tc_id: exec.tc_id, priority: exec.priority, steps: exec.steps, expected_result: exec.expected_result };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 680, maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        <div style={{ padding: '1.25rem 1.5rem', borderBottom: `1px solid ${theme.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 700 }}>Run Test Case</h2>
            <span style={{ fontSize: '0.78rem', color: theme.textMuted }}>{tc.tc_id} — {tc.title}</span>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem', color: '#9CA3AF' }}>×</button>
        </div>
        <div style={{ padding: '1.25rem 1.5rem', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {/* Steps viewer */}
          {tc.steps?.length > 0 && (
            <div>
              <label style={{ ...lbl, marginBottom: '0.5rem' }}>Test Steps</label>
              <div style={{ border: `1px solid ${theme.border}`, borderRadius: 8, overflow: 'hidden' }}>
                {tc.steps.map((s, i) => (
                  <div key={i} style={{ display: 'grid', gridTemplateColumns: '28px 1fr 1fr', gap: '0.5rem', padding: '0.6rem 0.75rem', borderBottom: i < tc.steps.length - 1 ? `1px solid #F3F4F6` : 'none', fontSize: '0.8rem', background: i % 2 === 0 ? '#FAFAFA' : '#fff' }}>
                    <span style={{ fontWeight: 700, color: theme.textMuted }}>{i + 1}</span>
                    <span>{s.step}</span>
                    <span style={{ color: theme.textMuted }}>{s.expected}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {tc.expected_result && (
            <div style={{ background: '#F0FDF4', border: '1px solid #DCFCE7', borderRadius: 8, padding: '0.75rem 1rem', fontSize: '0.8rem' }}>
              <strong>Expected Result:</strong> {tc.expected_result}
            </div>
          )}

          {/* Result entry */}
          <div>
            <label style={lbl}>Test Result *</label>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {Object.entries(EXEC_STATUS_META).map(([k, v]) => (
                <button key={k} onClick={() => setForm({ ...form, status: k })}
                  style={{ padding: '0.4rem 0.875rem', borderRadius: 7, border: `2px solid ${form.status === k ? v.color : theme.border}`, background: form.status === k ? v.bg : '#fff', color: form.status === k ? v.color : theme.textMuted, cursor: 'pointer', fontWeight: 700, fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 4 }}>
                  {v.icon} {v.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label style={lbl}>Actual Result</label>
            <textarea style={{ ...inp, minHeight: 70, resize: 'vertical' }} value={form.actual_result} onChange={e => setForm({ ...form, actual_result: e.target.value })} placeholder="What actually happened?" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
            <div>
              <label style={lbl}>Assigned To</label>
              <select style={inp} value={form.assigned_to} onChange={e => setForm({ ...form, assigned_to: e.target.value })}>
                <option value="">— Unassigned —</option>
                {members.map(m => <option key={m.id} value={m.employee_id}>{m.name || m.employee_name}</option>)}
              </select>
            </div>
            <div>
              <label style={lbl}>Time Spent (mins)</label>
              <input style={inp} type="number" min="0" value={form.time_spent_mins} onChange={e => setForm({ ...form, time_spent_mins: e.target.value })} placeholder="e.g. 30" />
            </div>
            <div>
              <label style={lbl}>Bug ID (if fail)</label>
              <input style={inp} type="number" min="0" value={form.bug_id} onChange={e => setForm({ ...form, bug_id: e.target.value })} placeholder="Bug #" />
            </div>
          </div>
          <div>
            <label style={lbl}>Comments</label>
            <textarea style={{ ...inp, minHeight: 60, resize: 'vertical' }} value={form.comments} onChange={e => setForm({ ...form, comments: e.target.value })} placeholder="Additional notes…" />
          </div>
        </div>
        <div style={{ padding: '1rem 1.5rem', borderTop: `1px solid ${theme.border}`, display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
          <button onClick={onClose} style={{ padding: '0.5rem 1.25rem', border: `1.5px solid ${theme.border}`, borderRadius: 7, background: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{ padding: '0.5rem 1.25rem', border: 'none', borderRadius: 7, background: theme.primary, color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem', opacity: saving ? 0.7 : 1 }}>{saving ? 'Saving…' : 'Save Result'}</button>
        </div>
      </div>
    </div>
  );
}

// ── Cycle Detail (executions view) ────────────────────────────────────────────
function CycleDetail({ cycle, project, modules, members, onBack, onCycleUpdated }) {
  const [executions, setExecutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [runModal, setRunModal] = useState(null);
  const [addCasesModal, setAddCasesModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');

  const load = useCallback(() => {
    setLoading(true);
    qa.getExecutions(cycle.id).then(r => setExecutions(r.data || [])).catch(() => setExecutions([])).finally(() => setLoading(false));
  }, [cycle.id]);

  useEffect(() => { load(); }, [load]);

  const existingTcIds = new Set(executions.map(e => e.test_case_id));
  const filtered = filterStatus === 'all' ? executions : executions.filter(e => e.status === filterStatus);

  const statusCounts = executions.reduce((acc, e) => {
    acc[e.status] = (acc[e.status] || 0) + 1;
    return acc;
  }, {});

  const handleRemove = async (tcId) => {
    if (!window.confirm('Remove this test case from the cycle?')) return;
    try { await qa.removeCaseFromCycle(cycle.id, tcId); load(); onCycleUpdated(); } catch { alert('Failed'); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', color: theme.textMuted, fontSize: '0.875rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
            ← Back
          </button>
          <span style={{ color: '#D1D5DB' }}>|</span>
          <div>
            <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 700 }}>{cycle.name}</h2>
            <div style={{ display: 'flex', gap: '0.4rem', marginTop: 2, alignItems: 'center' }}>
              <CycleBadge v={cycle.status} />
              {cycle.release_version && <span style={{ fontSize: '0.72rem', color: theme.textMuted, fontFamily: 'monospace' }}>v{cycle.release_version}</span>}
              {cycle.start_date && <span style={{ fontSize: '0.72rem', color: theme.textMuted }}>{cycle.start_date} → {cycle.end_date || '?'}</span>}
            </div>
          </div>
        </div>
        <button onClick={() => setAddCasesModal(true)}
          style={{ padding: '0.5rem 1rem', background: theme.primary, color: '#fff', border: 'none', borderRadius: 7, cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem' }}>
          + Add Test Cases
        </button>
      </div>

      {/* Progress summary */}
      <div style={card}>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
          {Object.entries(EXEC_STATUS_META).map(([k, v]) => (
            <div key={k} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '1.2rem', fontWeight: 800, color: v.color }}>{statusCounts[k] || 0}</div>
              <div style={{ fontSize: '0.72rem', color: theme.textMuted, fontWeight: 600 }}>{v.label}</div>
            </div>
          ))}
          <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
            <div style={{ fontSize: '1.2rem', fontWeight: 800, color: theme.text }}>{executions.length}</div>
            <div style={{ fontSize: '0.72rem', color: theme.textMuted, fontWeight: 600 }}>Total</div>
          </div>
        </div>
        <ProgressBar pass={statusCounts.pass || 0} fail={statusCounts.fail || 0} blocked={statusCounts.blocked || 0} retest={statusCounts.retest || 0} total={executions.length} />
      </div>

      {/* Filter chips */}
      <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
        {[{ k: 'all', label: `All (${executions.length})` }, ...Object.entries(EXEC_STATUS_META).map(([k, v]) => ({ k, label: `${v.label} (${statusCounts[k] || 0})` }))].map(({ k, label }) => (
          <button key={k} onClick={() => setFilterStatus(k)}
            style={{ padding: '0.3rem 0.75rem', border: `1.5px solid ${filterStatus === k ? theme.primary : theme.border}`, borderRadius: 9999, background: filterStatus === k ? '#EEF2FF' : '#fff', color: filterStatus === k ? theme.primary : theme.textMuted, cursor: 'pointer', fontSize: '0.78rem', fontWeight: filterStatus === k ? 700 : 400 }}>
            {label}
          </button>
        ))}
      </div>

      {/* Executions table */}
      <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
          <thead>
            <tr style={{ background: '#F8FAFC' }}>
              {['TC-ID', 'Title', 'Module', 'Priority', 'Type', 'Assigned To', 'Status', 'Time Spent', 'Bug', 'Actions'].map(h => (
                <th key={h} style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: 700, color: theme.textMuted, borderBottom: `2px solid ${theme.border}`, fontSize: '0.72rem', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={10} style={{ textAlign: 'center', padding: '2rem', color: theme.textMuted }}>Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={10} style={{ textAlign: 'center', padding: '2rem', color: theme.textMuted }}>No test cases in this view</td></tr>
            ) : filtered.map(ex => (
              <tr key={ex.id} style={{ borderBottom: `1px solid ${theme.border}` }}
                onMouseEnter={e => e.currentTarget.style.background = '#F8FAFC'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <td style={{ padding: '0.75rem 1rem', fontFamily: 'monospace', fontSize: '0.75rem', color: theme.textMuted, whiteSpace: 'nowrap' }}>{ex.tc_id}</td>
                <td style={{ padding: '0.75rem 1rem', fontWeight: 600, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ex.title}</td>
                <td style={{ padding: '0.75rem 1rem', color: theme.textMuted, whiteSpace: 'nowrap', fontSize: '0.8rem' }}>{ex.module_feature || '—'}</td>
                <td style={{ padding: '0.75rem 1rem' }}><PriBadge v={ex.priority} /></td>
                <td style={{ padding: '0.75rem 1rem' }}>{ex.test_case_id && <TypeBadge v={'functional'} />}</td>
                <td style={{ padding: '0.75rem 1rem', fontSize: '0.8rem', color: theme.textMuted, whiteSpace: 'nowrap' }}>{ex.assignee_name || '—'}</td>
                <td style={{ padding: '0.75rem 1rem' }}><ExecBadge v={ex.status} /></td>
                <td style={{ padding: '0.75rem 1rem', color: theme.textMuted, fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{ex.time_spent_mins ? `${ex.time_spent_mins}m` : '—'}</td>
                <td style={{ padding: '0.75rem 1rem', fontSize: '0.8rem' }}>{ex.bug_id ? <span style={{ color: '#DC2626', fontWeight: 600 }}>#{ex.bug_id}</span> : '—'}</td>
                <td style={{ padding: '0.75rem 1rem' }}>
                  <div style={{ display: 'flex', gap: '0.4rem' }}>
                    <button onClick={() => setRunModal(ex)}
                      style={{ padding: '0.3rem 0.7rem', border: `1.5px solid ${theme.primary}`, borderRadius: 6, background: '#EEF2FF', color: theme.primary, cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
                      ▶ Run
                    </button>
                    <button onClick={() => handleRemove(ex.test_case_id)}
                      style={{ padding: '0.3rem 0.7rem', border: `1.5px solid #FEE2E2`, borderRadius: 6, background: '#FEF2F2', color: '#DC2626', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600 }}>
                      ×
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {runModal && <RunModal exec={runModal} members={members} onClose={() => setRunModal(null)} onSaved={() => { setRunModal(null); load(); onCycleUpdated(); }} />}
      {addCasesModal && <AddCasesModal cycle={cycle} projectId={project.id} existingIds={existingTcIds} modules={modules} members={members} onClose={() => setAddCasesModal(false)} onSaved={() => { setAddCasesModal(false); load(); onCycleUpdated(); }} />}
    </div>
  );
}

// ── Main QA Component ─────────────────────────────────────────────────────────
export default function QA({ project }) {
  const [view, setView] = useState('cycles');   // 'cycles' | 'test-cases' | 'analytics'
  const [cycles, setCycles] = useState([]);
  const [testCases, setTestCases] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeCycle, setActiveCycle] = useState(null);
  const [tcModal, setTcModal] = useState(null);       // null | 'new' | tc object
  const [cycleModal, setCycleModal] = useState(null); // null | 'new' | cycle object
  const [search, setSearch] = useState('');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [modules, setModules] = useState([]);
  const [members, setMembers] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null); // null | result object
  const uploadRef = useRef(null);

  const loadCycles = useCallback(() => {
    qa.getCycles(project.id).then(r => setCycles(r.data || [])).catch(() => setCycles([]));
  }, [project.id]);

  const loadTestCases = useCallback(() => {
    setLoading(true);
    qa.getTestCases(project.id, { is_active: true }).then(r => setTestCases(r.data || [])).catch(() => setTestCases([])).finally(() => setLoading(false));
  }, [project.id]);

  const loadAnalytics = useCallback(() => {
    qa.getAnalytics(project.id).then(r => setAnalytics(r.data)).catch(() => {});
  }, [project.id]);

  useEffect(() => { loadCycles(); }, [loadCycles]);
  useEffect(() => { if (view === 'test-cases') loadTestCases(); }, [view, loadTestCases]);
  useEffect(() => { if (view === 'analytics') loadAnalytics(); }, [view, loadAnalytics]);
  useEffect(() => {
    pm.getModules(project.id).then(r => setModules(r.data || [])).catch(() => {});
    pm.getMembers(project.id).then(r => setMembers(r.data || [])).catch(() => {});
  }, [project.id]);

  const handleDeleteCycle = async (id) => {
    if (!window.confirm('Delete this test cycle?')) return;
    try { await qa.deleteCycle(id); loadCycles(); } catch { alert('Failed'); }
  };
  const handleDeleteTC = async (id) => {
    if (!window.confirm('Delete this test case?')) return;
    try { await qa.deleteTestCase(id); loadTestCases(); } catch { alert('Failed'); }
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await qa.downloadTemplate(project.id);
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      const a = document.createElement('a');
      a.href = url; a.download = 'test_cases_template.xlsx'; a.click();
      URL.revokeObjectURL(url);
    } catch { alert('Failed to download template'); }
  };

  const handleUploadExcel = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    setUploading(true);
    try {
      const res = await qa.bulkUploadCases(project.id, file);
      setUploadResult(res.data);
      loadTestCases();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  };

  const filteredCases = testCases.filter(tc => {
    const s = search.toLowerCase();
    const matchSearch = !search ||
      (tc.module_name || tc.module_feature || '').toLowerCase().includes(s) ||
      (tc.submodule_name || '').toLowerCase().includes(s) ||
      (tc.scenario_description || tc.title || '').toLowerCase().includes(s) ||
      (tc.tc_id || '').toLowerCase().includes(s);
    const matchPri = filterPriority === 'all' || tc.priority === filterPriority;
    const matchType = filterType === 'all' || tc.test_type === filterType;
    return matchSearch && matchPri && matchType;
  });

  if (activeCycle) {
    return (
      <div style={{ padding: '1.5rem' }}>
        <CycleDetail cycle={activeCycle} project={project} modules={modules} members={members} onBack={() => { setActiveCycle(null); loadCycles(); }} onCycleUpdated={loadCycles} />
      </div>
    );
  }

  return (
    <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      {/* Sub-tab nav */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div style={{ display: 'flex', gap: '0.25rem', background: '#F3F4F6', borderRadius: 8, padding: '3px' }}>
          {[{ k: 'cycles', label: '🔄 Test Cycles' }, { k: 'test-cases', label: '📋 Test Cases' }, { k: 'analytics', label: '📊 Analytics' }].map(t => (
            <button key={t.k} onClick={() => setView(t.k)}
              style={{ padding: '0.4rem 0.875rem', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: '0.8rem', fontWeight: view === t.k ? 700 : 500, background: view === t.k ? '#fff' : 'transparent', color: view === t.k ? theme.text : theme.textMuted, boxShadow: view === t.k ? '0 1px 4px rgba(0,0,0,0.08)' : 'none' }}>
              {t.label}
            </button>
          ))}
        </div>
        <div>
          {view === 'cycles' && (
            <button onClick={() => setCycleModal('new')}
              style={{ padding: '0.5rem 1rem', background: theme.primary, color: '#fff', border: 'none', borderRadius: 7, cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem' }}>
              + New Cycle
            </button>
          )}
          {view === 'test-cases' && (
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <button onClick={handleDownloadTemplate}
                style={{ padding: '0.5rem 0.875rem', border: `1.5px solid ${theme.border}`, borderRadius: 7, background: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: '0.8rem', color: theme.text, display: 'flex', alignItems: 'center', gap: 5 }}>
                ⬇ Sample Template
              </button>
              <button onClick={() => uploadRef.current?.click()} disabled={uploading}
                style={{ padding: '0.5rem 0.875rem', border: `1.5px solid ${theme.border}`, borderRadius: 7, background: uploading ? '#F3F4F6' : '#F0FDF4', cursor: uploading ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: '0.8rem', color: '#16A34A', display: 'flex', alignItems: 'center', gap: 5 }}>
                {uploading ? '⏳ Uploading…' : '⬆ Upload Excel'}
              </button>
              <input ref={uploadRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={handleUploadExcel} />
              <button onClick={() => setTcModal('new')}
                style={{ padding: '0.5rem 1rem', background: theme.primary, color: '#fff', border: 'none', borderRadius: 7, cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem' }}>
                + New Test Case
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── Cycles view ── */}
      {view === 'cycles' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
          {cycles.length === 0 ? (
            <div style={{ ...card, textAlign: 'center', padding: '3rem', color: theme.textMuted }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>🔄</div>
              <p style={{ fontWeight: 600, margin: '0 0 0.25rem' }}>No test cycles yet</p>
              <p style={{ fontSize: '0.875rem', margin: 0 }}>Create a cycle to group and run test cases.</p>
            </div>
          ) : cycles.map(c => (
            <div key={c.id} style={{ ...card, cursor: 'pointer' }} onClick={() => setActiveCycle(c)}
              onMouseEnter={e => e.currentTarget.style.borderColor = theme.primary}
              onMouseLeave={e => e.currentTarget.style.borderColor = theme.border}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem', marginBottom: '0.75rem' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 700 }}>{c.name}</h3>
                    <CycleBadge v={c.status} />
                    {c.release_version && <span style={{ fontSize: '0.72rem', fontFamily: 'monospace', color: theme.textMuted, background: '#F3F4F6', padding: '1px 6px', borderRadius: 4 }}>v{c.release_version}</span>}
                  </div>
                  {c.description && <p style={{ margin: '0.25rem 0 0', fontSize: '0.8rem', color: theme.textMuted }}>{c.description}</p>}
                  {(c.start_date || c.end_date) && (
                    <p style={{ margin: '0.2rem 0 0', fontSize: '0.75rem', color: theme.textMuted }}>{c.start_date} → {c.end_date || '—'}</p>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '0.4rem', flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                  <button onClick={() => setCycleModal(c)}
                    style={{ padding: '0.3rem 0.6rem', border: `1px solid ${theme.border}`, borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: '0.75rem', color: theme.textMuted, fontWeight: 600 }}>✏ Edit</button>
                  <button onClick={() => handleDeleteCycle(c.id)}
                    style={{ padding: '0.3rem 0.6rem', border: '1px solid #FEE2E2', borderRadius: 6, background: '#FEF2F2', cursor: 'pointer', fontSize: '0.75rem', color: '#DC2626', fontWeight: 600 }}>🗑</button>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '1rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                {Object.entries(EXEC_STATUS_META).map(([k, v]) => (
                  <span key={k} style={{ fontSize: '0.75rem', color: v.color, fontWeight: 700 }}>{v.icon} {c[k] || 0} {v.label}</span>
                ))}
                <span style={{ fontSize: '0.75rem', color: theme.textMuted, marginLeft: 'auto' }}>Total: {c.total_cases}</span>
              </div>
              <ProgressBar pass={c.pass} fail={c.fail} blocked={c.blocked} retest={c.retest} total={c.total_cases} />
            </div>
          ))}
        </div>
      )}

      {/* ── Test Cases view ── */}
      {view === 'test-cases' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
          {/* Filters */}
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            <input style={{ ...inp, width: 240 }} placeholder="Search test cases…" value={search} onChange={e => setSearch(e.target.value)} />
            <select style={{ ...inp, width: 140 }} value={filterPriority} onChange={e => setFilterPriority(e.target.value)}>
              <option value="all">All Priorities</option>
              {Object.entries(PRIORITY_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
            <select style={{ ...inp, width: 160 }} value={filterType} onChange={e => setFilterType(e.target.value)}>
              <option value="all">All Types</option>
              {Object.entries(TYPE_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
          <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr style={{ background: '#F8FAFC' }}>
                  {['TC ID', 'Module Name', 'Submodule Name', 'Scenario ID', 'Test Scenario Description', 'Priority', 'Test Type', 'Test Steps', 'Expected Results', 'Actions'].map(h => (
                    <th key={h} style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: 700, color: theme.textMuted, borderBottom: `2px solid ${theme.border}`, fontSize: '0.72rem', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={10} style={{ textAlign: 'center', padding: '2rem', color: theme.textMuted }}>Loading…</td></tr>
                ) : filteredCases.length === 0 ? (
                  <tr><td colSpan={10} style={{ textAlign: 'center', padding: '2rem', color: theme.textMuted }}>No test cases found</td></tr>
                ) : filteredCases.map(tc => (
                  <tr key={tc.id} style={{ borderBottom: `1px solid ${theme.border}`, verticalAlign: 'top' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#F8FAFC'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={{ padding: '0.75rem 1rem', fontFamily: 'monospace', fontSize: '0.75rem', color: theme.primary, whiteSpace: 'nowrap', fontWeight: 700 }}>{tc.tc_id}</td>
                    <td style={{ padding: '0.75rem 1rem', fontSize: '0.8rem', whiteSpace: 'nowrap', fontWeight: 500 }}>{tc.module_name || tc.module_feature || '—'}</td>
                    <td style={{ padding: '0.75rem 1rem', color: theme.textMuted, fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{tc.submodule_name || '—'}</td>
                    <td style={{ padding: '0.75rem 1rem', fontFamily: 'monospace', fontSize: '0.75rem', color: '#7C3AED', whiteSpace: 'nowrap', fontWeight: 700 }}>{tc.scenario_id_str || '—'}</td>
                    <td style={{ padding: '0.75rem 1rem', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.82rem' }} title={tc.scenario_description || tc.title}>
                      {(tc.scenario_description || tc.title || '—').slice(0, 40)}{(tc.scenario_description || tc.title || '').length > 40 ? '…' : ''}
                    </td>
                    <td style={{ padding: '0.75rem 1rem' }}><PriBadge v={tc.priority} /></td>
                    <td style={{ padding: '0.75rem 1rem' }}><TypeBadge v={tc.test_type} /></td>
                    {/* Test Steps */}
                    <td style={{ padding: '0.75rem 1rem', maxWidth: 260, fontSize: '0.78rem', color: theme.text }}>
                      {(tc.steps || []).length === 0 ? (
                        <span style={{ color: theme.textMuted }}>—</span>
                      ) : (
                        <ol style={{ margin: 0, paddingLeft: '1.1rem', display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                          {(tc.steps || []).map((s, i) => (
                            <li key={i} style={{ lineHeight: 1.4 }}>
                              <span>{s.step}</span>
                              {s.expected && <span style={{ color: theme.textMuted }}> → {s.expected}</span>}
                            </li>
                          ))}
                        </ol>
                      )}
                    </td>
                    {/* Expected Results */}
                    <td style={{ padding: '0.75rem 1rem', maxWidth: 220, fontSize: '0.78rem', color: theme.text }}>
                      {tc.expected_result
                        ? <span title={tc.expected_result}>{tc.expected_result.slice(0, 80)}{tc.expected_result.length > 80 ? '…' : ''}</span>
                        : <span style={{ color: theme.textMuted }}>—</span>
                      }
                    </td>
                    <td style={{ padding: '0.75rem 1rem', whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'flex', gap: '0.4rem' }}>
                        <button onClick={() => setTcModal(tc)}
                          style={{ padding: '0.3rem 0.6rem', border: `1px solid ${theme.border}`, borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600 }}>✏ Edit</button>
                        <button onClick={() => handleDeleteTC(tc.id)}
                          style={{ padding: '0.3rem 0.6rem', border: '1px solid #FEE2E2', borderRadius: 6, background: '#FEF2F2', cursor: 'pointer', fontSize: '0.75rem', color: '#DC2626', fontWeight: 600 }}>🗑</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Analytics view ── */}
      {view === 'analytics' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem' }}>
          {/* Summary cards */}
          {[
            { label: 'Total Test Cases', v: analytics?.total_cases ?? '—', color: '#2563EB', bg: '#DBEAFE' },
            { label: 'Total Cycles', v: analytics?.total_cycles ?? '—', color: '#7C3AED', bg: '#F5F3FF' },
            { label: 'Total Executions', v: analytics?.total_executions ?? '—', color: '#D97706', bg: '#FEF3C7' },
            { label: 'Pass Rate', v: analytics ? `${analytics.pass_rate}%` : '—', color: '#16A34A', bg: '#DCFCE7' },
          ].map(s => (
            <div key={s.label} style={{ ...card, display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <div style={{ width: 48, height: 48, borderRadius: 10, background: s.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ fontSize: '1.5rem', fontWeight: 800, color: s.color }}>{s.v}</span>
              </div>
              <span style={{ fontWeight: 600, color: theme.textMuted, fontSize: '0.875rem' }}>{s.label}</span>
            </div>
          ))}

          {/* Execution status breakdown */}
          {analytics?.execution_status && Object.keys(analytics.execution_status).length > 0 && (
            <div style={{ ...card, gridColumn: '1/-1' }}>
              <h3 style={{ margin: '0 0 1rem', fontSize: '0.9rem', fontWeight: 700 }}>Execution Status Breakdown</h3>
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                {Object.entries(EXEC_STATUS_META).map(([k, v]) => {
                  const count = analytics.execution_status[k] || 0;
                  const total = analytics.total_executions || 1;
                  const pct = Math.round(count / total * 100);
                  return (
                    <div key={k} style={{ flex: '1 1 120px', textAlign: 'center', padding: '0.75rem', background: v.bg, borderRadius: 8 }}>
                      <div style={{ fontSize: '1.5rem', fontWeight: 800, color: v.color }}>{count}</div>
                      <div style={{ fontSize: '0.75rem', fontWeight: 700, color: v.color }}>{v.label}</div>
                      <div style={{ fontSize: '0.7rem', color: theme.textMuted }}>{pct}%</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Priority distribution */}
          {analytics?.priority_distribution && Object.keys(analytics.priority_distribution).length > 0 && (
            <div style={card}>
              <h3 style={{ margin: '0 0 0.875rem', fontSize: '0.9rem', fontWeight: 700 }}>Test Cases by Priority</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {Object.entries(analytics.priority_distribution).map(([k, v]) => {
                  const m = PRIORITY_META[k] || { label: k, color: '#6B7280', bg: '#F3F4F6' };
                  const total = analytics.total_cases || 1;
                  return (
                    <div key={k}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                        <span style={{ fontSize: '0.8rem', fontWeight: 600, color: m.color }}>{m.label}</span>
                        <span style={{ fontSize: '0.8rem', color: theme.textMuted }}>{v}</span>
                      </div>
                      <div style={{ height: 6, background: '#F3F4F6', borderRadius: 9999, overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${(v / total) * 100}%`, background: m.color, borderRadius: 9999 }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Module distribution */}
          {analytics?.module_distribution && Object.keys(analytics.module_distribution).length > 0 && (
            <div style={card}>
              <h3 style={{ margin: '0 0 0.875rem', fontSize: '0.9rem', fontWeight: 700 }}>Test Cases by Module</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                {Object.entries(analytics.module_distribution).sort((a, b) => b[1] - a[1]).slice(0, 10).map(([mod, cnt]) => {
                  const total = analytics.total_cases || 1;
                  return (
                    <div key={mod}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                        <span style={{ fontSize: '0.8rem', fontWeight: 500 }}>{mod}</span>
                        <span style={{ fontSize: '0.8rem', color: theme.textMuted }}>{cnt}</span>
                      </div>
                      <div style={{ height: 5, background: '#F3F4F6', borderRadius: 9999, overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${(cnt / total) * 100}%`, background: theme.primary, borderRadius: 9999 }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modals */}
      {tcModal && (
        <TestCaseModal
          tc={tcModal === 'new' ? null : tcModal}
          projectId={project.id}
          onClose={() => setTcModal(null)}
          onSaved={() => { setTcModal(null); loadTestCases(); }}
        />
      )}
      {cycleModal && (
        <CycleModal
          cycle={cycleModal === 'new' ? null : cycleModal}
          projectId={project.id}
          onClose={() => setCycleModal(null)}
          onSaved={() => { setCycleModal(null); loadCycles(); }}
        />
      )}

      {/* ── Upload Result Modal ── */}
      {uploadResult && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
          onClick={e => e.target === e.currentTarget && setUploadResult(null)}>
          <div style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 540, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ padding: '1.25rem 1.5rem', borderBottom: `1px solid ${theme.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 700 }}>Upload Results</h2>
              <button onClick={() => setUploadResult(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem', color: '#9CA3AF' }}>×</button>
            </div>
            <div style={{ padding: '1.25rem 1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div style={{ display: 'flex', gap: '1rem' }}>
                <div style={{ flex: 1, background: '#DCFCE7', borderRadius: 10, padding: '1rem', textAlign: 'center' }}>
                  <div style={{ fontSize: '2rem', fontWeight: 800, color: '#16A34A' }}>{uploadResult.created}</div>
                  <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#16A34A' }}>Test Cases Created</div>
                </div>
                <div style={{ flex: 1, background: uploadResult.errors > 0 ? '#FEE2E2' : '#F3F4F6', borderRadius: 10, padding: '1rem', textAlign: 'center' }}>
                  <div style={{ fontSize: '2rem', fontWeight: 800, color: uploadResult.errors > 0 ? '#DC2626' : '#9CA3AF' }}>{uploadResult.errors}</div>
                  <div style={{ fontSize: '0.8rem', fontWeight: 600, color: uploadResult.errors > 0 ? '#DC2626' : '#9CA3AF' }}>Rows Skipped</div>
                </div>
              </div>
              {uploadResult.error_details?.length > 0 && (
                <div>
                  <p style={{ margin: '0 0 0.5rem', fontSize: '0.8rem', fontWeight: 600, color: '#DC2626' }}>Skipped rows:</p>
                  <div style={{ background: '#FEF2F2', border: '1px solid #FEE2E2', borderRadius: 8, padding: '0.75rem', maxHeight: 160, overflowY: 'auto' }}>
                    {uploadResult.error_details.map((e, i) => (
                      <div key={i} style={{ fontSize: '0.8rem', color: '#DC2626', padding: '0.2rem 0' }}>
                        Row {e.row}: {e.error}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {uploadResult.created_cases?.length > 0 && (
                <div>
                  <p style={{ margin: '0 0 0.5rem', fontSize: '0.8rem', fontWeight: 600, color: '#16A34A' }}>Created test cases:</p>
                  <div style={{ background: '#F0FDF4', border: '1px solid #DCFCE7', borderRadius: 8, padding: '0.75rem', maxHeight: 160, overflowY: 'auto' }}>
                    {uploadResult.created_cases.map((c, i) => (
                      <div key={i} style={{ fontSize: '0.8rem', color: '#15803D', padding: '0.2rem 0', display: 'flex', gap: '0.5rem' }}>
                        <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{c.tc_id}</span>
                        <span>{c.title}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div style={{ padding: '1rem 1.5rem', borderTop: `1px solid ${theme.border}`, display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={() => setUploadResult(null)}
                style={{ padding: '0.5rem 1.5rem', border: 'none', borderRadius: 7, background: theme.primary, color: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: '0.875rem' }}>
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
